import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notificationAuditService } from '../../services/api/notificationAudit.service';

/**
 * Fetch notification event types for filtering
 */
export const fetchNotificationEventTypes = createAsyncThunk(
  'notificationAudit/fetchEventTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAuditService.getEventTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch event types');
    }
  }
);

/**
 * Search notification audit logs with filters
 */
export const fetchNotificationAuditSearch = createAsyncThunk(
  'notificationAudit/fetchAuditSearch',
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationAuditService.searchNotificationAuditLogs(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notification audit logs');
    }
  }
);

/**
 * Get notification audit details by ID
 */
export const fetchNotificationAuditDetails = createAsyncThunk(
  'notificationAudit/fetchAuditDetails',
  async (auditLogId, { rejectWithValue }) => {
    try {
      const response = await notificationAuditService.getNotificationAuditDetails(auditLogId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch audit details');
    }
  }
);

/**
 * Get audit logs by notification ID
 */
export const fetchAuditLogsByNotificationId = createAsyncThunk(
  'notificationAudit/fetchByNotificationId',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await notificationAuditService.getAuditLogsByNotificationId(notificationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch audit logs');
    }
  }
);

/**
 * Export notification audit trail
 */
export const exportNotificationAuditTrail = createAsyncThunk(
  'notificationAudit/exportAuditTrail',
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationAuditService.exportNotificationAuditLogs(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export audit trail');
    }
  }
);

/**
 * Get notification audit statistics
 */
export const fetchNotificationAuditStatistics = createAsyncThunk(
  'notificationAudit/fetchStatistics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationAuditService.getNotificationAuditStatistics(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch statistics');
    }
  }
);

const initialState = {
  eventTypes: [],
  auditLogs: [],
  auditDetails: null,
  statistics: null,
  isLoading: false,
  isLoadingEventTypes: false,
  isLoadingAuditSearch: false,
  isLoadingDetails: false,
  isLoadingExport: false,
  isLoadingStatistics: false,
  error: null,
  errorEventTypes: null,
  errorAuditSearch: null,
  errorDetails: null,
  errorExport: null,
  errorStatistics: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

const notificationAuditSlice = createSlice({
  name: 'notificationAudit',
  initialState,
  reducers: {
    clearNotificationAuditError: (state) => {
      state.error = null;
      state.errorEventTypes = null;
      state.errorAuditSearch = null;
      state.errorDetails = null;
      state.errorExport = null;
      state.errorStatistics = null;
    },
    clearAuditDetails: (state) => {
      state.auditDetails = null;
    },
    resetNotificationAuditState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Event Types
      .addCase(fetchNotificationEventTypes.pending, (state) => {
        state.isLoadingEventTypes = true;
        state.errorEventTypes = null;
      })
      .addCase(fetchNotificationEventTypes.fulfilled, (state, action) => {
        state.isLoadingEventTypes = false;
        state.eventTypes = action.payload;
      })
      .addCase(fetchNotificationEventTypes.rejected, (state, action) => {
        state.isLoadingEventTypes = false;
        state.errorEventTypes = action.payload;
      })

      // Fetch Audit Search
      .addCase(fetchNotificationAuditSearch.pending, (state) => {
        state.isLoadingAuditSearch = true;
        state.errorAuditSearch = null;
      })
      .addCase(fetchNotificationAuditSearch.fulfilled, (state, action) => {
        state.isLoadingAuditSearch = false;
        state.auditLogs = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchNotificationAuditSearch.rejected, (state, action) => {
        state.isLoadingAuditSearch = false;
        state.errorAuditSearch = action.payload;
      })

      // Fetch Audit Details
      .addCase(fetchNotificationAuditDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.errorDetails = null;
      })
      .addCase(fetchNotificationAuditDetails.fulfilled, (state, action) => {
        state.isLoadingDetails = false;
        state.auditDetails = action.payload.data;
      })
      .addCase(fetchNotificationAuditDetails.rejected, (state, action) => {
        state.isLoadingDetails = false;
        state.errorDetails = action.payload;
      })

      // Fetch Audit Logs by Notification ID
      .addCase(fetchAuditLogsByNotificationId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogsByNotificationId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.auditLogs = action.payload.data || [];
      })
      .addCase(fetchAuditLogsByNotificationId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Export Audit Trail
      .addCase(exportNotificationAuditTrail.pending, (state) => {
        state.isLoadingExport = true;
        state.errorExport = null;
      })
      .addCase(exportNotificationAuditTrail.fulfilled, (state, action) => {
        state.isLoadingExport = false;
      })
      .addCase(exportNotificationAuditTrail.rejected, (state, action) => {
        state.isLoadingExport = false;
        state.errorExport = action.payload;
      })

      // Fetch Statistics
      .addCase(fetchNotificationAuditStatistics.pending, (state) => {
        state.isLoadingStatistics = true;
        state.errorStatistics = null;
      })
      .addCase(fetchNotificationAuditStatistics.fulfilled, (state, action) => {
        state.isLoadingStatistics = false;
        state.statistics = action.payload.data;
      })
      .addCase(fetchNotificationAuditStatistics.rejected, (state, action) => {
        state.isLoadingStatistics = false;
        state.errorStatistics = action.payload;
      });
  },
});

export const {
  clearNotificationAuditError,
  clearAuditDetails,
  resetNotificationAuditState,
} = notificationAuditSlice.actions;

export default notificationAuditSlice.reducer;
